/**
 * El botón de exportar de cada sección, con su período.
 *
 * ---------------------------------------------------------------------------
 *  Lo que pidió Jaime, y cómo se resolvió
 * ---------------------------------------------------------------------------
 *
 * «En cada botón debe existir forma de sacar un reporte por año, mes, día».
 *
 * En vez de repetir el botón en ocho pantallas —y olvidarlo en la novena—, vive en la
 * **cabecera**, que ya sabe en qué sección estás. Aparece solo donde hay algo que
 * exportar, y ofrece el período: **día, mes, año o todo**.
 *
 * ---------------------------------------------------------------------------
 *  La honestidad del período
 * ---------------------------------------------------------------------------
 *
 * No todos los informes tienen período, y fingir que sí sería mentir:
 *
 *  · **Movimientos** y **presupuestos** son de un período: se pueden filtrar por día,
 *    mes o año, y el archivo trae solo ese tramo.
 *  · **Deudas, fiado, cuentas, metas y categorías** son una **foto de ahora**: lo que
 *    debes hoy, lo que tienes hoy. Filtrarlas por fecha no daría «lo que debías el 3 de
 *    marzo», daría lo mismo de siempre con otra fecha en el nombre. Por eso el botón no
 *    enseña períodos para esas secciones, y lo dice.
 */

import { CalendarDays, Download, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { downloadFile } from '../lib/api';

/** Una opción del menú de período. */
interface PeriodOption {
  key: string;
  label: string;
  /** Los parámetros que se le mandan al servidor. */
  query: string;
}

/** El primer y el último día de un mes, en formato ISO corto. */
function monthRange(year: number, month: number): { from: string; to: string } {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const mm = String(month).padStart(2, '0');
  return {
    from: `${year}-${mm}-01`,
    to: `${year}-${mm}-${String(last).padStart(2, '0')}`,
  };
}

/** Las opciones de período, calculadas sobre hoy. */
function periodOptions(mode: 'month' | 'day'): PeriodOption[] {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = String(now.getDate()).padStart(2, '0');
  const mm = String(month).padStart(2, '0');

  const today = `${year}-${mm}-${day}`;
  const thisMonth = monthRange(year, month);

  if (mode === 'day') {
    return [
      { key: 'day', label: 'Hoy', query: `?from=${today}&to=${today}` },
      { key: 'month', label: 'Este mes', query: `?from=${thisMonth.from}&to=${thisMonth.to}` },
      { key: 'year', label: 'Este año', query: `?from=${year}-01-01&to=${year}-12-31` },
      { key: 'all', label: 'Todo', query: '' },
    ];
  }

  return [
    { key: 'month', label: 'Este mes', query: `?monthKey=${year}-${mm}` },
    { key: 'year', label: `Todo ${year}`, query: `?from=${year}-01-01&to=${year}-12-31` },
    { key: 'all', label: 'Todo', query: '' },
  ];
}

/** Qué se puede exportar en cada sección. */
const SECTION_REPORTS: Record<
  string,
  { entity: string; label: string; mode: 'month' | 'day' | 'none' }
> = {
  '/movimientos': { entity: 'movimientos', label: 'movimientos', mode: 'day' },
  '/presupuestos': { entity: 'presupuestos', label: 'presupuestos', mode: 'month' },
  '/deudas': { entity: 'deudas', label: 'deudas', mode: 'none' },
  '/fiado': { entity: 'fiado', label: 'fiado', mode: 'none' },
  '/recurrentes': { entity: 'gastos-fijos', label: 'gastos fijos', mode: 'none' },
  '/metas': { entity: 'metas', label: 'metas', mode: 'none' },
  '/cuentas': { entity: 'cuentas', label: 'cuentas', mode: 'none' },
};

export function SectionExport(): React.JSX.Element | null {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const report = SECTION_REPORTS[location.pathname];

  // Se cierra al tocar fuera, que es lo que espera cualquiera.
  useEffect(() => {
    if (!open) return undefined;

    const onDown = (event: MouseEvent): void => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!report) return null;

  const download = async (query: string): Promise<void> => {
    setBusy(true);
    setError(false);
    setOpen(false);

    try {
      await downloadFile(
        `/api/reports/export/${report.entity}${query}`,
        `eldescuadre-${report.entity}.csv`,
      );
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  // Un informe que es una foto de ahora: no se le ofrecen períodos, porque no los tiene.
  if (report.mode === 'none') {
    return (
      <button
        type="button"
        className="ed-btn-ghost !min-h-[40px] !px-2"
        aria-label={`Exportar ${report.label} a CSV`}
        title={`Exportar ${report.label} a CSV (foto de ahora mismo)`}
        disabled={busy}
        onClick={() =>
          void download('').catch(() => {
            setError(true);
          })
        }
      >
        {busy ? (
          <Loader2 size={18} className="animate-spin" aria-hidden="true" />
        ) : error ? (
          <span className="text-[11px] text-ed-coral-light">Error</span>
        ) : (
          <Download size={18} aria-hidden="true" />
        )}
      </button>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        className="ed-btn-ghost !min-h-[40px] !px-2"
        aria-label={`Exportar ${report.label} a CSV`}
        aria-expanded={open}
        title={`Exportar ${report.label} a CSV`}
        disabled={busy}
        onClick={() => setOpen((value) => !value)}
      >
        {busy ? (
          <Loader2 size={18} className="animate-spin" aria-hidden="true" />
        ) : (
          <CalendarDays size={18} aria-hidden="true" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-1 w-48 overflow-hidden rounded-ed border border-ed-bg-border/50 bg-ed-bg-card shadow-ed">
          <p className="border-b border-ed-bg-border/40 px-3 py-2 text-[11px] text-ed-text-muted">
            Exportar {report.label}
          </p>
          {periodOptions(report.mode).map((option) => (
            <button
              key={option.key}
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-ed-bg-elevated/50"
              onClick={() => void download(option.query)}
            >
              <Download size={14} aria-hidden="true" />
              {option.label}
            </button>
          ))}
        </div>
      )}

      {error && (
        <p role="alert" className="absolute right-0 top-full mt-1 whitespace-nowrap text-[11px] text-ed-coral-light">
          No se pudo exportar
        </p>
      )}
    </div>
  );
}
